import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  login as rbacLogin, challenge as rbacChallenge, resendLoginOtp, getProfile,
} from '@/services/rbac-auth.service';
import { useAuthStore } from '@/stores/auth.store';
import { ROUTES } from '@/constants';
import { EMAIL_OTP_LENGTH, TOTP_LENGTH } from '@/lib/otp';
import type { ChallengeInfo, ChallengeType } from '@/types/rbac-auth.types';
import { isAxiosError } from 'axios';

export type LoginStep = 'credentials' | 'new-password' | 'otp' | 'mfa-setup';

/** Normaliza CUSTOM_CHALLENGE → EMAIL_OTP y mapea challengeType → paso. */
function resolveStep(raw: ChallengeInfo['challengeType']): { step: LoginStep; type: ChallengeType } {
  const type: ChallengeType = raw === 'CUSTOM_CHALLENGE' ? 'EMAIL_OTP' : raw;
  switch (type) {
    case 'NEW_PASSWORD_REQUIRED': return { step: 'new-password', type };
    case 'EMAIL_OTP':
    case 'SMS_OTP':
    case 'SOFTWARE_TOKEN_MFA':    return { step: 'otp', type };
    case 'MFA_SETUP':             return { step: 'mfa-setup', type };
    default:                      return { step: 'credentials', type };
  }
}

export function useLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [challengeType, setChallengeType] = useState<ChallengeType | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const otpLength = challengeType === 'SOFTWARE_TOKEN_MFA' ? TOTP_LENGTH : EMAIL_OTP_LENGTH;
  const canResend = challengeType === 'EMAIL_OTP' || challengeType === 'SMS_OTP';

  const finish = useCallback(async () => {
    const profile = await getProfile();
    useAuthStore.getState().setFromProfile(profile);
    navigate(ROUTES.DASHBOARD, { replace: true });
  }, [navigate]);

  const applyChallenge = useCallback((c: ChallengeInfo) => {
    const { step: s, type } = resolveStep(c.challengeType);
    if (s === 'credentials') { setError('Flujo de verificación no soportado.'); return; }
    setChallengeType(type); setSession(c.session); setStep(s);
  }, []);

  const submitCredentials = useCallback(async (id: string, password: string) => {
    setError(null); setLoading(true); setIdentifier(id);
    try {
      const res = await rbacLogin(id, password);
      if (res.challenge) applyChallenge(res.challenge);
      else await finish();
    } catch (e) {
      setError(isAxiosError(e) && e.response?.status === 401 ? 'Usuario y/o contraseña incorrectas.' : 'Error al iniciar sesión.');
      throw e;
    } finally { setLoading(false); }
  }, [applyChallenge, finish]);

  const submitChallenge = useCallback(async (answer: string) => {
    if (!session || !challengeType) return;
    setError(null); setLoading(true);
    try {
      const key = challengeType === 'NEW_PASSWORD_REQUIRED' ? 'NEW_PASSWORD' : 'ANSWER';
      const res = await rbacChallenge({
        email: identifier, session, challengeType, challengeResponses: { [key]: answer },
      });
      if (res.nextChallenge) applyChallenge(res.nextChallenge);
      else await finish();
    } catch {
      setError('Código inválido o expirado.');
    } finally { setLoading(false); }
  }, [session, challengeType, identifier, applyChallenge, finish]);

  const resend = useCallback(async () => {
    if (!canResend) return;
    try {
      const res = await resendLoginOtp(identifier);
      if (res.session) setSession(res.session);
    } catch {
      setError('No se pudo reenviar el código. Volvé a iniciar sesión.');
      setStep('credentials'); setChallengeType(null); setSession(null);
    }
  }, [canResend, identifier]);

  const backToCredentials = useCallback(() => {
    setStep('credentials'); setChallengeType(null); setSession(null); setError(null);
  }, []);

  return {
    step, challengeType, otpLength, canResend, error, loading,
    submitCredentials, submitChallenge, resend, backToCredentials,
  };
}
